export interface EmitterEvent {
  result: boolean
  stop: boolean
  out?: any
}

export type EmitterHandler = (e: EmitterEvent, ...args: any[]) => void

export interface IEmitter {
  on(event: string, handler: EmitterHandler): void

  off(event: string, handler?: () => void): void

  one(event: string, handler: EmitterHandler): void

  trigger(event: string, ...data: any[]): void
}

export default class Emitter implements IEmitter {
  private handlers: {
    [key: string]: Array<(e: EmitterEvent, ...args: any[]) => void>
  }

  constructor() {
    this.handlers = {}
  }

  public on(event: string, handler: EmitterHandler) {
    if (!this.handlers[event]) {
      this.handlers[event] = [handler]
    } else {
      this.handlers[event].push(handler)
    }
    return this
  }

  public off(event: string, handler?: () => void) {
    if (this.handlers[event]) {
      if (!handler) {
        delete this.handlers[event]
      } else {
        for (let i = 0; i < this.handlers[event].length; i++) {
          const h = this.handlers[event][i]
          if (h === handler) {
            this.handlers[event].splice(i, 1)
          }
        }
      }
    }
    return this
  }

  public one(event: string, handler: EmitterHandler) {
    const oneTime = (...args: any[]) => {
      handler.apply(this, args)
      return this.off(event, oneTime)
    }
    return this.on(event, oneTime)
  }

  public trigger(event: string, ...data: any[]) {
    const evt: EmitterEvent = {
      result: true,
      stop: false,
    }

    if (this.handlers[event]) {
      for (const h of this.handlers[event]) {
        if (!evt.stop) {
          evt.out = h.apply(this, [evt].concat(data))
          evt.result = evt.result && (typeof evt.out === 'undefined' ? true : !!evt.out)
        }
      }
    }
    return evt
  }

  protected proxy(event: string, fromEmitter: Emitter, asEvent = event) {
    fromEmitter.on(event, (_e, ...args) => this.trigger(asEvent, ...args))
  }
}
